/**
 * @fileoverview System Status dashboard — operational health-at-a-glance for
 *   MongoDB, APIs, tenant clusters, Azure Container Apps, and Application
 *   Insights, plus a read-only Container Apps scaling-trend analysis panel.
 * @module system-web/pages/SystemStatusPage
 *
 * Responsibilities:
 * - Render live system health from the systemStatusService via hooks.
 * - Surface App Insights health metrics + 1h sparklines and a config-driven
 *   "Open in Application Insights" deep-link.
 * - Surface Container Apps platform metrics (replicas/restarts/CPU/memory),
 *   recent scaling/KEDA events, and 7/30/90-day replica trends.
 *
 * Dependencies:
 * - useSystemStatusPolling / useScalingTrend (healthPollingService).
 * - getWebConfig (portal deep-link URL — never hardcoded).
 *
 * Architecture:
 * - Local-first data-access: this page NEVER calls fetch/apiClient. All data
 *   arrives through hooks that delegate to the service layer. The System
 *   Status feed is a recognized inherently-online read (see systemStatusService).
 *
 * Notes:
 * - Every Azure-sourced metric degrades gracefully to "not configured" when the
 *   backend cannot reach Azure Monitor / Log Analytics.
 */
import * as React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, RefreshCw, Pause, Play, ChevronDown, ChevronUp, CheckCircle, AlertTriangle, XCircle, Database, Server, Cpu, HardDrive, Zap, Globe, Clock, Layers, ExternalLink, TrendingUp, RotateCcw } from 'lucide-react';
import { useScalingTrend, useSystemStatusPolling } from '../services/healthPollingService';
import type { StatusSnapshot, OverallHealth, AtlasNode, ApiHealth, ContainerAppStatus, TenantClusterSummary, MetricPoint, AppInsightsHealth, ScalingEvent, ScalingTrendPoint, ScalingTrendRange } from '../services/systemStatusService';
import { getWebConfig } from '../config';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}
function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
function timeSince(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function HealthBadge({ health }: { health: OverallHealth }) {
  const map = {
    healthy:  { icon: CheckCircle,    color: '#16a34a', bg: 'rgba(22,163,74,0.1)',  label: 'Healthy'  },
    degraded: { icon: AlertTriangle,  color: '#d97706', bg: 'rgba(217,119,6,0.1)',  label: 'Degraded' },
    critical: { icon: XCircle,        color: '#dc2626', bg: 'rgba(220,38,38,0.1)',  label: 'Critical' },
  };
  const { icon: Icon, color, bg, label } = map[health];
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: bg, color }}>
      <Icon className="h-3.5 w-3.5" />{label}
    </span>
  );
}

function StatCard({ label, value, sub, icon: Icon }: { label: string; value: string; sub?: string; icon: React.ElementType }) {
  return (
    <div className="flex-1 min-w-0 rounded-lg p-3 border" style={{ borderColor: 'rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.02)' }}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-3.5 w-3.5 opacity-50" />
        <span className="text-xs opacity-60 font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-xl font-bold tabular-nums">{value}</p>
      {sub && <p className="text-xs opacity-50 mt-0.5">{sub}</p>}
    </div>
  );
}

function SectionHeader({ title, icon: Icon, expanded, onToggle, badge }: {
  title: string; icon: React.ElementType; expanded: boolean; onToggle: () => void; badge?: React.ReactNode;
}) {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-3 px-4 py-3 text-left"
      style={{ borderBottom: expanded ? '1px solid rgba(0,0,0,0.06)' : 'none' }}
    >
      <Icon className="h-4 w-4 opacity-60" />
      <span className="font-semibold text-sm flex-1">{title}</span>
      {badge}
      {expanded ? <ChevronUp className="h-4 w-4 opacity-40" /> : <ChevronDown className="h-4 w-4 opacity-40" />}
    </button>
  );
}

function SectionCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border mb-4 overflow-hidden ${className}`} style={{ borderColor: 'rgba(0,0,0,0.08)', background: 'var(--card-bg, white)' }}>
      {children}
    </div>
  );
}

function ApiRow({ api }: { api: ApiHealth }) {
  const color = api.status === 'healthy' ? '#16a34a' : api.status === 'degraded' ? '#d97706' : '#dc2626';
  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b last:border-0" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
      <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: color }} />
      <span className="text-sm font-medium w-32">{api.name}</span>
      <span className="text-sm" style={{ color }}>{api.status.charAt(0).toUpperCase() + api.status.slice(1)}</span>
      <span className="text-xs opacity-50 ml-auto">{api.latencyMs}ms · {api.url || 'localhost'}</span>
    </div>
  );
}

function AtlasNodeRow({ node }: { node: AtlasNode }) {
  const isPrimary = node.role === 'PRIMARY';
  const cpuColor = node.cpuPercent > 80 ? '#dc2626' : node.cpuPercent > 60 ? '#d97706' : '#16a34a';
  return (
    <div className="flex items-center gap-4 px-4 py-2.5 border-b last:border-0 text-sm" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
      <span className="w-20 font-medium text-xs px-2 py-0.5 rounded" style={{
        background: isPrimary ? 'rgba(245,158,11,0.15)' : 'rgba(0,0,0,0.06)',
        color: isPrimary ? '#92400e' : 'inherit',
      }}>{node.role}</span>
      <span className="text-xs opacity-60 truncate flex-1">{node.host}</span>
      <span className="tabular-nums font-semibold" style={{ color: cpuColor }}>{node.cpuPercent.toFixed(1)}% CPU</span>
      <span className="tabular-nums opacity-60">{node.memPercent.toFixed(1)}% mem</span>
      <span className="tabular-nums opacity-60">{node.diskUsedPercent.toFixed(1)}% disk</span>
      {node.replicationLagSeconds > 0 && (
        <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(220,38,38,0.1)', color: '#dc2626' }}>
          lag {node.replicationLagSeconds}s
        </span>
      )}
    </div>
  );
}

function Sparkline({ snapshots, field, color = '#f59e0b', height = 32 }: {
  snapshots: StatusSnapshot[];
  field: 'connections' | 'atlasCpu' | 'opcounters';
  color?: string;
  height?: number;
}) {
  if (snapshots.length < 2) return <span className="text-xs opacity-40">Collecting data…</span>;
  const values = snapshots.map(s => {
    if (field === 'connections') return s.connections.current;
    if (field === 'atlasCpu') return s.atlasCpuPercent ?? 0;
    return s.opcounters.query + s.opcounters.insert + s.opcounters.update;
  });
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 120;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={height} className="opacity-80">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function TenantClusterCard({ cluster }: { cluster: TenantClusterSummary }) {
  const pct = cluster.maxDatabases > 0
    ? Math.min((cluster.currentDatabaseCount / cluster.maxDatabases) * 100, 100)
    : 0;
  const statusColor = cluster.status === 'active' ? '#16a34a'
    : cluster.status === 'full' ? '#d97706'
    : cluster.status === 'maintenance' ? '#6b7280'
    : '#dc2626';
  return (
    <div className="px-4 py-3 border-b last:border-0" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
      <div className="flex items-center gap-3 mb-2">
        <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: statusColor }} />
        <span className="text-sm font-semibold">{cluster.name}</span>
        <span className="text-xs opacity-50">{cluster.region}</span>
        <span className="text-xs px-2 py-0.5 rounded ml-auto" style={{ background: 'rgba(0,0,0,0.06)' }}>
          {cluster.status}
        </span>
      </div>
      <div className="flex items-center gap-3 mb-1">
        <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.08)' }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'rgba(245,158,11,0.6)' }} />
        </div>
        <span className="text-xs tabular-nums opacity-70 w-28 text-right">
          {cluster.currentDatabaseCount} / {cluster.maxDatabases} tenants
        </span>
      </div>
      {cluster.atlas ? (
        <div className="mt-2 rounded-lg overflow-hidden" style={{ border: '1px solid rgba(0,0,0,0.06)' }}>
          {cluster.atlas.nodes.map((node, i) => <AtlasNodeRow key={i} node={node} />)}
        </div>
      ) : (
        <p className="text-xs opacity-40 mt-1">Atlas monitoring not available for this cluster</p>
      )}
    </div>
  );
}

function ContainerAppRow({ app }: { app: ContainerAppStatus }) {
  const statusColor = app.status === 'running' ? '#16a34a' : app.status === 'degraded' ? '#d97706' : app.status === 'down' ? '#dc2626' : '#6b7280';
  const shortRevision = app.activeRevision.split('--').slice(-1)[0] ?? app.activeRevision;
  const shortLatest = app.latestRevision.split('--').slice(-1)[0] ?? app.latestRevision;
  const revisionMismatch = !!app.latestRevision && app.activeRevision !== app.latestRevision;
  const hasMetrics =
    app.restartCount24h != null || app.cpuUsageCores != null || app.memoryUsageBytes != null ||
    app.rxBytes24h != null || app.txBytes24h != null || (app.replicaSeries?.length ?? 0) > 0;
  return (
    <div className="px-4 py-3 border-b last:border-0 text-sm" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
      <div className="flex items-center gap-4">
        <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: statusColor }} />
        <span className="font-medium w-56 truncate">{app.name}</span>
        <span className="w-20" style={{ color: statusColor }}>{app.runningState}</span>
        <span className="text-xs opacity-50 font-mono truncate flex-1" title={app.activeRevision}>
          {shortRevision}
          {revisionMismatch && (
            <span className="ml-1.5 px-1 py-0.5 rounded" style={{ background: 'rgba(217,119,6,0.12)', color: '#92400e' }} title={`Latest: ${app.latestRevision}`}>
              → {shortLatest}
            </span>
          )}
        </span>
        <span className="text-xs tabular-nums opacity-60">{app.replicaCount} replica{app.replicaCount !== 1 ? 's' : ''}</span>
      </div>
      {hasMetrics && (
        <div className="flex items-center gap-5 mt-2 pl-6 flex-wrap">
          {app.restartCount24h != null && (
            <span className="text-xs opacity-60">
              <span className="opacity-70">Restarts (24h) </span>
              <span className="font-semibold tabular-nums" style={{ color: app.restartCount24h > 0 ? '#d97706' : undefined }}>{app.restartCount24h}</span>
            </span>
          )}
          {app.cpuUsageCores != null && (
            <span className="text-xs opacity-60"><span className="opacity-70">CPU </span><span className="font-semibold tabular-nums">{app.cpuUsageCores.toFixed(2)} cores</span></span>
          )}
          {app.memoryUsageBytes != null && (
            <span className="text-xs opacity-60"><span className="opacity-70">Mem </span><span className="font-semibold tabular-nums">{formatBytes(app.memoryUsageBytes)}</span></span>
          )}
          {(app.rxBytes24h != null || app.txBytes24h != null) && (
            <span className="text-xs opacity-60">
              <span className="opacity-70">Net (24h) </span>
              <span className="font-semibold tabular-nums">↓{formatBytes(app.rxBytes24h ?? 0)} ↑{formatBytes(app.txBytes24h ?? 0)}</span>
            </span>
          )}
          {(app.replicaSeries?.length ?? 0) > 1 && (
            <span className="inline-flex items-center gap-1.5">
              <span className="text-xs opacity-50">Replicas (24h)</span>
              <MetricSparkline points={app.replicaSeries!} color="#16a34a" width={90} height={20} />
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * @component MetricSparkline
 * Purpose:     Render a compact line for an arbitrary time series (MetricPoint[]).
 * Props:       points, color?, height?, width?.
 * Behavior:    Scales values to fit; shows a placeholder when < 2 points.
 * Data Source: local (values passed in from hook state).
 * Constraints: presentation-only; no data access.
 * Performance: single inline SVG polyline.
 */
function MetricSparkline({ points, color = '#f59e0b', height = 28, width = 110 }: {
  points: MetricPoint[]; color?: string; height?: number; width?: number;
}) {
  if (!points || points.length < 2) return <span className="text-xs opacity-40">n/a</span>;
  const values = points.map(p => p.v);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} className="opacity-80">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function fmtMs(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
  return `${Math.round(ms)}ms`;
}

/**
 * @component AppInsightsSection
 * Purpose:     Application Insights health-at-a-glance (request/failure/response/
 *              exception/dependency/page-load) with 1h sparklines + portal link.
 * Props:       ai (health data or null), portalUrl (deep-link base or '').
 * Behavior:    Renders "not configured" guidance when ai is null; hides the
 *              "Open in Application Insights" button when portalUrl is empty.
 * Data Source: local (from useSystemStatusPolling → systemStatusService).
 * Constraints: no direct API calls; portal URL comes from config, never hardcoded.
 * Performance: static cards + small SVG sparklines.
 */
function AppInsightsSection({ ai, portalUrl }: { ai: AppInsightsHealth | null; portalUrl: string }) {
  if (!ai) {
    return (
      <p className="px-4 py-3 text-sm opacity-50">
        Set AZURE_LOG_ANALYTICS_WORKSPACE_ID on system-api (and grant its managed identity the
        Monitoring Reader role) to enable Application Insights health metrics.
      </p>
    );
  }
  const failColor = ai.failedRequestRatePct > 5 ? '#dc2626' : ai.failedRequestRatePct > 1 ? '#d97706' : '#16a34a';
  const depColor = ai.dependencyFailureRatePct > 5 ? '#dc2626' : ai.dependencyFailureRatePct > 1 ? '#d97706' : '#16a34a';
  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Request rate" value={`${ai.requestRatePerMin.toFixed(1)}/min`} sub={`last ${ai.windowMinutes}m`} icon={Activity} />
        <div className="flex-1 min-w-0 rounded-lg p-3 border" style={{ borderColor: 'rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.02)' }}>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-3.5 w-3.5 opacity-50" />
            <span className="text-xs opacity-60 font-medium uppercase tracking-wide">Failed requests</span>
          </div>
          <p className="text-xl font-bold tabular-nums" style={{ color: failColor }}>{ai.failedRequestRatePct.toFixed(1)}%</p>
          <p className="text-xs opacity-50 mt-0.5">of requests</p>
        </div>
        <StatCard label="Avg response" value={fmtMs(ai.avgResponseTimeMs)} sub={`last ${ai.windowMinutes}m`} icon={Clock} />
        <StatCard label="Exceptions" value={fmtNum(ai.exceptionCount5m)} sub={`last ${ai.windowMinutes}m`} icon={XCircle} />
        <div className="flex-1 min-w-0 rounded-lg p-3 border" style={{ borderColor: 'rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.02)' }}>
          <div className="flex items-center gap-2 mb-1">
            <Server className="h-3.5 w-3.5 opacity-50" />
            <span className="text-xs opacity-60 font-medium uppercase tracking-wide">Dependency failures</span>
          </div>
          <p className="text-xl font-bold tabular-nums" style={{ color: depColor }}>{ai.dependencyFailureRatePct.toFixed(1)}%</p>
          <p className="text-xs opacity-50 mt-0.5">of calls</p>
        </div>
        <StatCard label="Page load" value={ai.pageLoadTimeMs !== null ? fmtMs(ai.pageLoadTimeMs) : 'n/a'} sub="browser (RUM)" icon={Globe} />
      </div>

      <div className="flex gap-6 flex-wrap items-end">
        <div>
          <p className="text-xs opacity-50 mb-1">Requests/min (1h)</p>
          <MetricSparkline points={ai.series.requestsPerMin} color="#6366f1" />
        </div>
        <div>
          <p className="text-xs opacity-50 mb-1">Failure % (1h)</p>
          <MetricSparkline points={ai.series.failedRatePct} color="#dc2626" />
        </div>
        <div>
          <p className="text-xs opacity-50 mb-1">Response ms (1h)</p>
          <MetricSparkline points={ai.series.responseTimeMs} color="#f59e0b" />
        </div>
        <div>
          <p className="text-xs opacity-50 mb-1">Exceptions (1h)</p>
          <MetricSparkline points={ai.series.exceptions} color="#b91c1c" />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <p className="text-xs opacity-40">Updated {timeSince(ai.checkedAt)}</p>
        {portalUrl && (
          <a
            href={portalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors"
            style={{ borderColor: 'rgba(0,0,0,0.12)', color: '#6366f1' }}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open in Application Insights
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * @component ScalingEventRow
 * Purpose:     One recent scaling / KEDA / probe event.
 * Props:       ev (ScalingEvent).
 * Behavior:    Colors the reason chip by severity keyword.
 * Data Source: local (from container-apps status feed).
 * Constraints: presentation-only.
 * Performance: trivial.
 */
function ScalingEventRow({ ev }: { ev: ScalingEvent }) {
  const isFailure = /fail|error|unhealthy/i.test(`${ev.reason} ${ev.message}`);
  const chipColor = isFailure ? '#dc2626' : /down|deactiv/i.test(ev.reason) ? '#d97706' : '#16a34a';
  return (
    <div className="flex items-center gap-3 px-4 py-2 border-b last:border-0 text-sm" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
      <span className="text-xs opacity-50 w-20 flex-shrink-0 tabular-nums">{timeSince(ev.timestamp)}</span>
      <span className="text-xs font-medium px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: 'rgba(0,0,0,0.05)', color: chipColor }}>{ev.reason}</span>
      <span className="text-xs opacity-50 w-40 truncate flex-shrink-0">{ev.app}</span>
      <span className="text-xs opacity-70 truncate flex-1" title={ev.message}>{ev.message}</span>
    </div>
  );
}

/**
 * @component ScalingTrendSection
 * Purpose:     Read-only replica-count trend (avg + max) over 7/30/90 days to
 *              inform minReplicas/maxReplicas tuning — never mutates config.
 * Props:       (none — route-level panel).
 * Behavior:    Range toggle + reload drive the useScalingTrend hook; the panel
 *              never calls the API itself.
 * Data Source: local (useScalingTrend → systemStatusService).
 * Constraints: analysis only; scaling config lives in Terraform.
 * Performance: per-app sparklines from small aggregated series.
 */
function ScalingTrendSection() {
  const { trend, loading, error, range, setRange, reload } = useScalingTrend('7d');
  const ranges: ScalingTrendRange[] = ['7d', '30d', '90d'];
  const appNames = trend ? Object.keys(trend.trends).sort() : [];
  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        {ranges.map(r => (
          <button
            key={r}
            onClick={() => setRange(r)}
            className="text-xs px-2.5 py-1 rounded-lg border font-medium transition-colors"
            style={{
              borderColor: 'rgba(0,0,0,0.12)',
              background: range === r ? 'rgba(99,102,241,0.1)' : undefined,
              color: range === r ? '#6366f1' : undefined,
            }}
          >
            {r}
          </button>
        ))}
        <span className="text-xs opacity-50 ml-2">{trend ? `${trend.granularity} granularity` : ''}</span>
        <button
          onClick={reload}
          disabled={loading}
          className="ml-auto inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border font-medium"
          style={{ borderColor: 'rgba(0,0,0,0.12)' }}
        >
          <RotateCcw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          Reload
        </button>
      </div>

      {error && <p className="text-xs" style={{ color: '#dc2626' }}>{error}</p>}
      {!error && !loading && appNames.length === 0 && (
        <p className="text-sm opacity-50">
          No scaling trend data. Requires Azure Monitor access (AZURE_LOG_ANALYTICS_WORKSPACE_ID + Monitoring Reader) in a deployed environment.
        </p>
      )}

      {appNames.map(name => {
        const series = trend!.trends[name];
        const maxSeries: MetricPoint[] = series.map((p: ScalingTrendPoint) => ({ t: p.t, v: p.replicasMax }));
        const peak = series.reduce((m, p) => Math.max(m, p.replicasMax), 0);
        const avg = series.length ? series.reduce((s, p) => s + p.replicasAvg, 0) / series.length : 0;
        return (
          <div key={name} className="flex items-center gap-4 px-1 py-2 border-b last:border-0" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
            <span className="text-sm font-medium w-56 truncate">{name}</span>
            <MetricSparkline points={maxSeries} color="#6366f1" width={160} />
            <span className="text-xs tabular-nums opacity-70 ml-auto">avg {avg.toFixed(1)}</span>
            <span className="text-xs tabular-nums font-semibold">peak {peak}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * @component SystemStatusPage
 *
 * Purpose:
 * - Render the system status dashboard for the system administration portal.
 *
 * Props: (none — route component)
 *
 * Behavior:
 * - Polls system health endpoints and displays overall status, container apps,
 *   MongoDB Atlas nodes, API health, and databases.
 * - Supports auto-refresh, manual refresh, and expanding/collapsing sections.
 *
 * Data Source: system status polling service (online)
 * Constraints: no direct API calls; data comes from service hook.
 * Performance: polling interval controlled by useSystemStatusPolling.
 */
export default function SystemStatusPage() {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState({ mongodb: true, apis: true, containerApps: true, databases: true, appInsights: true, scalingTrend: false });
  const portalUrl = getWebConfig().appInsightsPortalUrl;
  const {
    data: statusPayload,
    error,
    lastUpdated,
    loading,
    refresh,
  } = useSystemStatusPolling(autoRefresh);

  const toggle = (key: keyof typeof expanded) =>
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const fetchData = async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      await refresh();
    } finally {
      if (isManual) setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-64">
        <RefreshCw className="h-6 w-6 animate-spin opacity-40" />
        <span className="ml-3 opacity-60">Collecting system metrics…</span>
      </div>
    );
  }

  if (error && !statusPayload.status) {
    return (
      <div className="p-8">
        <div className="rounded-xl border p-6 flex items-start gap-3" style={{ borderColor: 'rgba(220,38,38,0.3)', background: 'rgba(220,38,38,0.05)' }}>
          <XCircle className="h-5 w-5 mt-0.5" style={{ color: '#dc2626' }} />
          <div>
            <p className="font-semibold text-sm">Failed to load system status</p>
            <p className="text-xs opacity-70 mt-1">{error}</p>
          </div>
          <button onClick={() => fetchData(true)} className="ml-auto text-xs px-3 py-1.5 rounded-lg font-medium" style={{ background: 'rgba(220,38,38,0.1)', color: '#dc2626' }}>Retry</button>
        </div>
      </div>
    );
  }

  const data = statusPayload.status;
  const history = statusPayload.history;
  const mongo = data?.mongodb;
  const apis = data?.apis ?? [];
  const health = data?.overallHealth ?? 'healthy';
  const tenantClusters = mongo?.tenantClusters ?? [];
  const connPct = mongo ? Math.round((mongo.connections.current / Math.max(mongo.connections.current + mongo.connections.available, 1)) * 100) : 0;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Stale-data notice: the last poll failed but a previous status is still
          on screen — make clear it is not live. */}
      {error && data && (
        <div className="mb-4 rounded-xl border p-3 flex items-start gap-3" style={{ borderColor: 'rgba(217,119,6,0.4)', background: 'rgba(217,119,6,0.08)' }}>
          <AlertTriangle className="h-4 w-4 mt-0.5" style={{ color: '#d97706' }} />
          <div>
            <p className="font-semibold text-xs" style={{ color: '#b45309' }}>Status refresh failed — showing last known readings{lastUpdated ? ` from ${timeSince(lastUpdated)}` : ''}</p>
            <p className="text-xs opacity-70 mt-0.5">{error}</p>
          </div>
          <button onClick={() => fetchData(true)} className="ml-auto text-xs px-3 py-1.5 rounded-lg font-medium" style={{ background: 'rgba(217,119,6,0.12)', color: '#b45309' }}>Retry</button>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Activity className="h-5 w-5" style={{ color: '#f59e0b' }} />
            System Status
          </h1>
          {lastUpdated && <p className="text-xs opacity-50 mt-0.5">Updated {timeSince(lastUpdated)}</p>}
        </div>
        <HealthBadge health={health} />
        <Link
          to="/mongodb"
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors"
          style={{ borderColor: 'rgba(0,0,0,0.12)' }}
          title="Open the dedicated MongoDB dashboard"
        >
          <Database className="h-3.5 w-3.5" />
          MongoDB
        </Link>
        <button
          onClick={() => setAutoRefresh(p => !p)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors"
          style={{ borderColor: 'rgba(0,0,0,0.12)', color: autoRefresh ? '#16a34a' : undefined }}
          title={autoRefresh ? 'Auto-refresh on (30s) — click to pause' : 'Auto-refresh paused — click to resume'}
        >
          {autoRefresh ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          {autoRefresh ? '30s' : 'Paused'}
        </button>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors"
          style={{ borderColor: 'rgba(0,0,0,0.12)' }}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary row */}
      {mongo && (
        <div className="grid grid-cols-4 gap-3 mb-4">
          <StatCard label="MongoDB" value={mongo.version} sub={`Up ${mongo.uptimeFormatted}`} icon={Database} />
          <StatCard label="Connections" value={`${mongo.connections.current}`} sub={`${connPct}% of available`} icon={Server} />
          <StatCard label="Tenant DBs" value={String(mongo.tenantDbCount)} sub="active databases" icon={HardDrive} />
          <StatCard label="APIs" value={`${apis.filter(a => a.status === 'healthy').length}/${apis.length}`} sub="services healthy" icon={Globe} />
        </div>
      )}

      {/* MongoDB Server section — server-wide stats (connections, opcounters,
          network, memory) reflect the shared mongod process/cluster that
          hosts the SYSTEM database for this environment. This is distinct
          from the tenant data clusters shown below, which are separate
          Atlas clusters entirely. */}
      <SectionCard>
        <SectionHeader
          title="MongoDB Server — System Database"
          icon={Database}
          expanded={expanded.mongodb}
          onToggle={() => toggle('mongodb')}
          badge={data?.environment ? <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(245,158,11,0.1)', color: '#92400e' }}>{data.environment}</span> : undefined}
        />
        {expanded.mongodb && mongo && (
          <div className="p-4 space-y-4">
            <p className="text-xs opacity-50 -mt-1">
              Server hosting this environment's system database ({mongo.host}). Tenant data clusters are shown separately below.
            </p>
            {/* Key metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard label="Uptime" value={mongo.uptimeFormatted} sub={`v${mongo.version}`} icon={Clock} />
              <StatCard label="Connections" value={`${mongo.connections.current} / ${mongo.connections.current + mongo.connections.available}`} sub={`${connPct}% used`} icon={Server} />
              <StatCard label="Memory (resident)" value={`${mongo.mem.resident} MB`} sub={`${mongo.mem.virtual} MB virtual`} icon={Cpu} />
              <StatCard label="System Database" value={fmtNum(mongo.systemDb.documents)} sub={`docs · ${formatBytes(mongo.systemDb.dataSize)}`} icon={Database} />
            </div>

            {/* Op counters */}
            <div>
              <p className="text-xs font-semibold opacity-60 uppercase tracking-wide mb-2">Op Counters (since restart)</p>
              <div className="flex gap-4 flex-wrap">
                {Object.entries(mongo.opcounters).map(([k, v]) => (
                  <div key={k} className="text-sm">
                    <span className="opacity-50 capitalize">{k}</span>{' '}
                    <span className="font-semibold tabular-nums">{fmtNum(v)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Network */}
            <div>
              <p className="text-xs font-semibold opacity-60 uppercase tracking-wide mb-2">Network I/O (cumulative)</p>
              <div className="flex gap-6">
                <div className="text-sm"><span className="opacity-50">In </span><span className="font-semibold tabular-nums">{formatBytes(mongo.network.bytesIn)}</span></div>
                <div className="text-sm"><span className="opacity-50">Out </span><span className="font-semibold tabular-nums">{formatBytes(mongo.network.bytesOut)}</span></div>
              </div>
            </div>

            {/* Sparklines if history available */}
            {history.length >= 2 && (
              <div className="flex gap-8 items-end">
                <div>
                  <p className="text-xs opacity-50 mb-1">Connections (24h)</p>
                  <Sparkline snapshots={history} field="connections" color="#6366f1" />
                </div>
                <div>
                  <p className="text-xs opacity-50 mb-1">Ops/sample (24h)</p>
                  <Sparkline snapshots={history} field="opcounters" color="#f59e0b" />
                </div>
                {history.some(s => s.atlasCpuPercent !== null) && (
                  <div>
                    <p className="text-xs opacity-50 mb-1">Atlas CPU % (24h)</p>
                    <Sparkline snapshots={history} field="atlasCpu" color="#16a34a" />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* APIs section */}
      <SectionCard>
        <SectionHeader
          title="API Services"
          icon={Globe}
          expanded={expanded.apis}
          onToggle={() => toggle('apis')}
          badge={
            <span className="text-xs opacity-60 mr-2">
              {apis.filter(a => a.status === 'healthy').length}/{apis.length} healthy
            </span>
          }
        />
        {expanded.apis && (
          <div>
            {apis.length === 0
              ? <p className="px-4 py-3 text-sm opacity-50">No API health data available</p>
              : apis.map((api, i) => <ApiRow key={i} api={api} />)
            }
          </div>
        )}
      </SectionCard>

      {/* Tenant Data Clusters — scoped to the CURRENT app environment only.
          Production never shows dev/stage clusters and vice versa, per the
          `clusters` registry's `environment` field. */}
      {mongo && (
        <SectionCard>
          <SectionHeader
            title={`Tenant Data Clusters — ${data?.environment ?? ''}`}
            icon={HardDrive}
            expanded={expanded.databases}
            onToggle={() => toggle('databases')}
            badge={<span className="text-xs opacity-60 mr-2">{tenantClusters.length} cluster{tenantClusters.length !== 1 ? 's' : ''} · {mongo.tenantDbCount} tenants</span>}
          />
          {expanded.databases && (
            <div>
              {tenantClusters.length === 0
                ? <p className="px-4 py-3 text-sm opacity-50">No tenant clusters registered for this environment</p>
                : tenantClusters.map(cluster => <TenantClusterCard key={cluster.name} cluster={cluster} />)
              }
            </div>
          )}
        </SectionCard>
      )}

      {/* Container Apps section */}
      {data?.containerApps ? (
        <SectionCard>
          <SectionHeader
            title={data.containerApps.environment === 'local-docker'
              ? 'Containers — Docker Desktop (local)'
              : `Container Apps — ${data.containerApps.environment}`}
            icon={Layers}
            expanded={expanded.containerApps}
            onToggle={() => toggle('containerApps')}
            badge={
              <span className="text-xs opacity-60 mr-2">
                {data.containerApps.apps.filter(a => a.status === 'running').length}/{data.containerApps.apps.length} running
              </span>
            }
          />
          {expanded.containerApps && (
            <div>
              {data.containerApps.apps.length === 0
                ? <p className="px-4 py-3 text-sm opacity-50">No container apps found in environment</p>
                : data.containerApps.apps
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    // De-duplicate by name defensively — the backend should
                    // already aggregate replicas into one row per service,
                    // but a stable key with an index fallback prevents the
                    // duplicate-key React warning if a future backend change
                    // ever re-introduces same-named rows.
                    .map((app, i) => <ContainerAppRow key={`${app.name}-${i}`} app={app} />)
              }
              {data.containerApps.scalingEvents && data.containerApps.scalingEvents.length > 0 && (
                <div className="mt-1 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                  <p className="px-4 pt-3 pb-1 text-xs font-semibold opacity-60 uppercase tracking-wide">Recent scaling &amp; KEDA events</p>
                  {data.containerApps.scalingEvents.map((ev, i) => <ScalingEventRow key={i} ev={ev} />)}
                </div>
              )}
              <p className="px-4 py-2 text-xs opacity-40">Updated {timeSince(data.containerApps.checkedAt)}</p>
            </div>
          )}
        </SectionCard>
      ) : (
        <SectionCard>
          <SectionHeader title="Container Apps — Not Configured" icon={Layers} expanded={false} onToggle={() => {}} />
          <p className="px-4 py-3 text-sm opacity-50">Set AZURE_SUBSCRIPTION_ID, AZURE_RESOURCE_GROUP, and AZURE_CONTAINER_APP_ENVIRONMENT on system-api to enable live container monitoring.</p>
        </SectionCard>
      )}

      {/* Application Insights — request/failure/response/exception/dependency
          health + 1h sparklines + config-driven portal deep-link. */}
      <SectionCard>
        <SectionHeader
          title="Application Insights"
          icon={Zap}
          expanded={expanded.appInsights}
          onToggle={() => toggle('appInsights')}
          badge={
            <span className="text-xs opacity-60 mr-2">
              {data?.appInsights ? `${data.appInsights.failedRequestRatePct.toFixed(1)}% failed` : 'not configured'}
            </span>
          }
        />
        {expanded.appInsights && <AppInsightsSection ai={data?.appInsights ?? null} portalUrl={portalUrl} />}
      </SectionCard>

      {/* Scaling trend — read-only replica history for minReplicas/maxReplicas
          tuning; collapsed by default (issues an on-demand query). */}
      <SectionCard>
        <SectionHeader
          title="Container Apps — Scaling Trend"
          icon={TrendingUp}
          expanded={expanded.scalingTrend}
          onToggle={() => toggle('scalingTrend')}
        />
        {expanded.scalingTrend && <ScalingTrendSection />}
      </SectionCard>
    </div>
  );
}
